import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { RichTextEditor, theme, ThemeProvider } from '@m/alchemy-ui';

declare global {
  interface Window {
    TEXT_EDITOR_COMPONENTS: {
      addError: (name: string, errorMsg: string, inputtedText: string) => void;
      clearErrors: () => void;
      initTextEdit: () => void;
    };
  }
}

type CrowdicityTextEditorProps = {
  initialValue: string;
  errorMessage?: string;
};

const buttons = ['bold', 'italic', 'ul', '|', 'undo', 'redo'];
const defaultActionOnPaste = 'insert_clear_HTML';
const crowdicityTextEditorConfig = { defaultActionOnPaste, buttons };

export const CrowdicityTextEditor = ({ initialValue, errorMessage }: CrowdicityTextEditorProps) => {
  const [, setValue] = useState('');
  const [value] = useState(initialValue);

  return (
    <ThemeProvider theme={theme}>
      <>
        {/* Alchemy wants <GlobalStyles /> here, but including global styles will conflict with existing crowdicity styling */}
        <RichTextEditor
          onChange={(val) => setValue(val)}
          config={crowdicityTextEditorConfig}
          status={
            errorMessage
              ? {
                  level: 'error',
                  message: (
                    // ignoring a11y eslint rules here, we want the focus indicator shows up on the error messages for bofa customer
                    // eslint-disable-next-line
                    <div role="text" tabIndex={0}>
                      {errorMessage}
                    </div>
                  )
                }
              : undefined
          }
          defaultValue={value}
        />
      </>
    </ThemeProvider>
  );
};

// Add error to a field
export const addError = (name: string, errorMsg: string | undefined, inputtedText: string) => {
  const errorField = document.querySelector('[name=' + name + '],#' + name);
  const domNode = document.createElement('div');
  errorField?.parentNode?.insertBefore(domNode, errorField?.nextSibling);
  domNode.id = errorField?.id || '';
  domNode.className += 'alchemy-text-edit ';
  domNode.setAttribute('name', errorField?.getAttribute('name') || '');

  if (errorField?.className.indexOf('alchemy-character-limit') !== -1) {
    domNode.className += 'alchemy-character-limit';
    domNode.dataset.alchemyCharacterLimit = (errorField as HTMLElement).dataset.alchemyCharacterLimit;
  }

  errorField?.remove();
  renderNode(inputtedText, domNode, errorMsg);
};

// Remove all errors, used when attempting to submitting form
export const clearErrors = () => {
  const textInputs = document.querySelectorAll('.alchemy-text-edit');
  renderTextInputs(textInputs, false);
};

// initialize alchemy text editors in form
export const initTextEdit = () => {
  const textInputs = document.querySelectorAll('.alchemy-text-edit');
  renderTextInputs(textInputs, true);
};

// render components where needed
export const renderTextInputs = (textInputs: NodeListOf<Element> | HTMLElement[], isInitial: boolean) => {
  for (const textInput of textInputs) {
    const domNode = document.createElement('div');
    textInput.parentNode?.insertBefore(domNode, textInput.nextSibling);

    const value =
      (isInitial ? textInput.textContent : textInput.querySelector('[contenteditable=true]')?.innerHTML) || '';
    domNode.id = textInput.id;

    // Adding this class indicates that the textarea is now alchemified
    domNode.className += 'alchemy-text-edit ';
    domNode.setAttribute('name', textInput.getAttribute('name') || '');

    // This class indicates that the textarea in main has a character limit
    if (textInput.className.includes('GB_characterLimit')) {
      domNode.className += 'alchemy-character-limit ';
      domNode.dataset.alchemyCharacterLimit = textInput.getAttribute('data-gbcharacterlimit') || '';
    } else if (textInput.className.includes('alchemy-character-limit')) {
      domNode.className += 'alchemy-character-limit ';
      domNode.dataset.alchemyCharacterLimit = (textInput as HTMLElement).dataset.alchemyCharacterLimit;
    }

    textInput.remove();
    renderNode(value, domNode);
  }
};

const renderNode = (initialValue: string, domNode: HTMLDivElement, errorMsg?: string) => {
  ReactDOM.render(<CrowdicityTextEditor initialValue={initialValue} errorMessage={errorMsg} />, domNode);
};

window.TEXT_EDITOR_COMPONENTS = {
  addError,
  clearErrors,
  initTextEdit
};
